import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import PropTypes from 'prop-types';
import ReactDom from 'react-dom';
import { useTranslation } from 'react-i18next';
import './index.scss';
import Card from '../Card';
import Avatar from '../../atoms/Avatar';
import Button from '../../atoms/Button';
import ConfirmDeleteDialog from '../ConfirmDeleteDialog';
import { userProfileService } from '../../../services/userProfileService';
import PopupOverlay from '../../organisms/Background/PopupOverlay';
import { getCroppedImageFile } from '../../../utils/imageCrop';
import CircularImageCropper from '../ImageCropper';
import { resolveGatewayBackedServiceBaseUrl } from '../../../services/serviceBaseUrl';

const MEDIA_SERVICE_URL = resolveGatewayBackedServiceBaseUrl(process.env.REACT_APP_MEDIA_SERVICE_URL);
const BYTES_PER_MEGABYTE = 1024 * 1024;
const DEFAULT_IMAGE_MAX_SIZE_MB = 5;
const PROFILE_COVER_MAX_SIZE_MB = 20;

const resolveMaxSizeMb = (value) => {
    const numericValue = Number(value);
    return Number.isFinite(numericValue) && numericValue > 0
        ? numericValue
        : DEFAULT_IMAGE_MAX_SIZE_MB;
};

const toDisplayUrl = (url) => {
    if (!url) return null;
    if (url.startsWith('http://') || url.startsWith('https://')) {
        return url;
    }
    return `${MEDIA_SERVICE_URL}${url}`;
};

const MEDIA_CONFIG = {
    profilePicture: {
        i18nKey: 'profilePicture',
        maxSizeMb: DEFAULT_IMAGE_MAX_SIZE_MB,
        getHistory: userProfileService.getUserPhotos,
        upload: userProfileService.uploadProfilePicture,
        selectExisting: userProfileService.useExistingProfilePicture,
        deleteItem: userProfileService.deleteProfilePhoto,
        cropperProps: {
            size: 220,
            aspect: 1,
            cropShape: 'round',
        },
        thumbnailClassName: '',
        renderThumbnail: (displayUrl, altText) => (
            <Avatar
                src={displayUrl}
                alt={altText}
                className="profile-picture-upload__avatar"
            />
        ),
    },
    profileCover: {
        i18nKey: 'profileCover',
        maxSizeMb: PROFILE_COVER_MAX_SIZE_MB,
        getHistory: userProfileService.getUserCovers,
        upload: userProfileService.uploadProfileCover,
        selectExisting: userProfileService.useExistingProfileCover,
        deleteItem: userProfileService.deleteProfileCoverPhoto,
        cropperProps: {
            width: 360,
            height: 160,
            aspect: 9 / 4,
            cropShape: 'rect',
            className: 'profile-picture-upload__cover-cropper',
        },
        thumbnailClassName: 'photo-item--cover',
        renderThumbnail: (displayUrl, altText) => (
            <img
                src={displayUrl}
                alt={altText}
                className="profile-picture-upload__cover-thumb"
            />
        ),
    },
};

const ProfilePictureUpload = ({ onClose, onUploadSuccess, currentUrl, mode, mediaConfig }) => {
    const resolvedMode = MEDIA_CONFIG[mode] ? mode : 'profilePicture';
    const baseConfig = MEDIA_CONFIG[resolvedMode];
    const config = useMemo(() => ({
        ...baseConfig,
        ...(mediaConfig || {}),
        cropperProps: {
            ...(baseConfig.cropperProps || {}),
            ...(mediaConfig?.cropperProps || {}),
        },
    }), [baseConfig, mediaConfig]);
    const i18nKey = config.i18nKey;
    const maxSizeMb = resolveMaxSizeMb(config.maxSizeMb);
    const maxSizeBytes = maxSizeMb * BYTES_PER_MEGABYTE;
    const [userPhotos, setUserPhotos] = useState([]);
    const [selectedFile, setSelectedFile] = useState(null);
    const [selectedObjectKey, setSelectedObjectKey] = useState(null);
    const [previewUrl, setPreviewUrl] = useState(toDisplayUrl(currentUrl));
    const [initialPhoto, setInitialPhoto] = useState({
        objectKey: null,
        url: toDisplayUrl(currentUrl),
    });
    const [hasChanges, setHasChanges] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [error, setError] = useState(null);
    const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);
    const [cropResetKey, setCropResetKey] = useState(0);
    // The photo awaiting a confirmed delete. Non-null while the confirmation
    // dialog is open; its error lives beside it so a failed delete is reported
    // in the dialog rather than behind it, and the user can retry.
    const [photoPendingDeletion, setPhotoPendingDeletion] = useState(null);
    const [deleteError, setDeleteError] = useState(null);
    const objectUrlRef = useRef(null);
    const fileInputRef = useRef(null);
    const { t } = useTranslation();

    const isSameAsInitial = (objectKey, displayUrl) => {
        if (initialPhoto.objectKey && objectKey) {
            return initialPhoto.objectKey === objectKey;
        }
        return (initialPhoto.url || null) === (displayUrl || null);
    };

    const revokeObjectUrlIfNeeded = useCallback(() => {
        if (objectUrlRef.current) {
            URL.revokeObjectURL(objectUrlRef.current);
            objectUrlRef.current = null;
        }
    }, []);

    const resetCropState = useCallback(() => {
        setCroppedAreaPixels(null);
        setCropResetKey((prev) => prev + 1);
    }, []);

    useEffect(() => () => {
        revokeObjectUrlIfNeeded();
    }, [revokeObjectUrlIfNeeded]);

    useEffect(() => {
        const display = toDisplayUrl(currentUrl);
        setPreviewUrl(display);
        setInitialPhoto((prev) => ({ ...prev, url: display }));
        resetCropState();
    }, [currentUrl, resetCropState]);

    const fetchUserPhotos = useCallback(async () => {
        try {
            setIsLoading(true);
            const photos = typeof config.getHistory === 'function'
                ? await config.getHistory()
                : [];
            const normalized = Array.isArray(photos) ? photos : [];
            setUserPhotos(normalized);
            setError(null);

            const currentPhoto = normalized.find((photo) => photo.current) || normalized[0] || null;
            const displayUrl = currentPhoto ? toDisplayUrl(currentPhoto.url) : toDisplayUrl(currentUrl);
            const objectKey = currentPhoto?.objectKey || null;

            revokeObjectUrlIfNeeded();
            setSelectedFile(null);
            setSelectedObjectKey(objectKey);
            setPreviewUrl(displayUrl || null);
            setInitialPhoto({ objectKey, url: displayUrl || null });
            setHasChanges(false);
            resetCropState();

            return { photos: normalized, currentPhoto };
        } catch (err) {
            setError(t(`${i18nKey}.errors.load`));
            console.error('Error fetching photos:', err);
            return { photos: [], currentPhoto: null };
        } finally {
            setIsLoading(false);
        }
    }, [config, currentUrl, i18nKey, revokeObjectUrlIfNeeded, resetCropState, t]);

    useEffect(() => {
        fetchUserPhotos();
    }, [fetchUserPhotos]);

    const handleFileSelect = (event) => {
        const file = event.target.files[0];
        if (file) {
            if (file.size > maxSizeBytes) {
                setError(t(`${i18nKey}.errors.fileSize`, { max: maxSizeMb }));
                event.target.value = '';
                return;
            }
            if (!file.type.startsWith('image/')) {
                setError(t(`${i18nKey}.errors.invalidType`));
                event.target.value = '';
                return;
            }
            revokeObjectUrlIfNeeded();
            const objectUrl = URL.createObjectURL(file);
            objectUrlRef.current = objectUrl;
            setSelectedFile(file);
            setSelectedObjectKey(null);
            setPreviewUrl(objectUrl);
            setError(null);
            setHasChanges(true);
            resetCropState();
        }
        event.target.value = '';
    };

    const handleFileButtonClick = () => {
        if (!isSubmitting) {
            fileInputRef.current?.click();
        }
    };

    const handlePhotoSelect = (photo) => {
        if (!photo) return;
        const displayUrl = toDisplayUrl(photo.url);
        revokeObjectUrlIfNeeded();
        resetCropState();
        setSelectedFile(null);
        setSelectedObjectKey(photo.objectKey);
        setPreviewUrl(displayUrl);
        setError(null);
        setHasChanges(!isSameAsInitial(photo.objectKey, displayUrl));
    };

    const handleUpload = async () => {
        if (!previewUrl) {
            setError(t(`${i18nKey}.errors.selectPhoto`));
            return;
        }

        try {
            setIsSubmitting(true);
            let response;
            const shouldApplyCrop = Boolean(croppedAreaPixels) && Boolean(selectedFile);

            if (selectedFile) {
                const fileName = selectedFile.name || `profile-${Date.now()}`;
                const mimeType = selectedFile.type || 'image/jpeg';
                const fileToUpload = shouldApplyCrop
                    ? await getCroppedImageFile(previewUrl, croppedAreaPixels, fileName, { mimeType })
                    : selectedFile;
                if (fileToUpload.size > maxSizeBytes) {
                    setError(t(`${i18nKey}.errors.fileSize`, { max: maxSizeMb }));
                    return;
                }
                const formData = new FormData();
                formData.append('file', fileToUpload);
                response = await config.upload(formData, { selectedFile: fileToUpload });
            } else if (selectedObjectKey) {
                response = await config.selectExisting(selectedObjectKey);
            } else {
                setError(t(`${i18nKey}.errors.chooseImage`));
                setIsSubmitting(false);
                return;
            }

            if (response.url) {
                onUploadSuccess(response.url, response);
                onClose();
            } else {
                setError(t(`${i18nKey}.errors.profileUrl`));
            }
        } catch (err) {
            if (err.response && err.response.status === 401) {
                setError(t(`${i18nKey}.errors.sessionExpired`));
            } else {
                setError(t(`${i18nKey}.errors.update`));
            }
            console.error('Error updating profile picture:', err);
        } finally {
            setIsSubmitting(false);
        }
    };

    const canDeletePhotos = typeof config.deleteItem === 'function';

    const requestPhotoDeletion = (photo) => {
        if (!photo || !photo.objectKey || isSubmitting || !canDeletePhotos) {
            return;
        }
        setDeleteError(null);
        setPhotoPendingDeletion(photo);
    };

    const cancelPhotoDeletion = () => {
        setPhotoPendingDeletion(null);
        setDeleteError(null);
    };

    const handleDeletePhoto = async () => {
        const photo = photoPendingDeletion;
        if (!photo || !photo.objectKey || isSubmitting || !canDeletePhotos) {
            return;
        }

        try {
            setIsSubmitting(true);
            setDeleteError(null);
            const response = await config.deleteItem(photo.objectKey);
            const { currentPhoto } = await fetchUserPhotos();
            const latestUrl = response?.url || currentPhoto?.url || null;
            const displayUrl = currentPhoto ? toDisplayUrl(currentPhoto.url) : toDisplayUrl(currentUrl);

            setInitialPhoto({
                objectKey: currentPhoto?.objectKey || null,
                url: displayUrl || null,
            });
            setSelectedObjectKey(currentPhoto?.objectKey || null);
            setSelectedFile(null);
            setPreviewUrl(displayUrl || null);
            setHasChanges(false);
            setPhotoPendingDeletion(null);

            onUploadSuccess(latestUrl);
        } catch (err) {
            console.error('Error deleting profile photo:', err);
            setDeleteError(t(`${i18nKey}.errors.delete`));
        } finally {
            setIsSubmitting(false);
        }
    };

    const updateDisabled = isSubmitting || isLoading || !hasChanges;

    return ReactDom.createPortal(
        <>
            <PopupOverlay>
                <Card
                    className={`profile-picture-upload-card image-upload-modal-card profile-picture-upload-card--${resolvedMode}`}
                    title={t(`${i18nKey}.title`)}
                    body={
                        <div className="profile-picture-upload-content image-upload-modal-content">
                            {error && <div className="error-message" role="alert">{error}</div>}
                            {isLoading && <div className="loading-message">{t(`${i18nKey}.loading`)}</div>}

                            <div className="preview-section image-upload-modal-preview">
                                <CircularImageCropper
                                    image={previewUrl}
                                    disabled={isSubmitting}
                                    interactive={Boolean(selectedFile)}
                                    resetSignal={cropResetKey}
                                    onCropComplete={setCroppedAreaPixels}
                                    {...config.cropperProps}
                                />
                            </div>

                            <div className="upload-section image-upload-modal-upload-section">
                                <input
                                    ref={fileInputRef}
                                    type="file"
                                    accept="image/*"
                                    onChange={handleFileSelect}
                                    className="image-upload-modal__input"
                                    disabled={isSubmitting}
                                />
                                <Button
                                    variant="primary"
                                    shape="pill"
                                    className="image-upload-modal__button image-upload-modal__button--upload"
                                    onClick={handleFileButtonClick}
                                    disabled={isSubmitting}
                                >
                                    {t(`${i18nKey}.uploadNew`)}
                                </Button>
                            </div>

                            {userPhotos.length > 0 ? (
                                <div className="existing-photos">
                                    <h3>{t(`${i18nKey}.sectionTitle`)}</h3>
                                    <div className="photo-strip">
                                        {userPhotos.map((photo) => {
                                            const displayUrl = toDisplayUrl(photo.url);
                                            const isSelected = selectedObjectKey === photo.objectKey && !selectedFile;
                                            return (
                                                <button
                                                    key={photo.objectKey}
                                                    type="button"
                                                    className={`photo-item ${config.thumbnailClassName} ${isSelected ? 'selected' : ''}`}
                                                    onClick={() => handlePhotoSelect(photo)}
                                                    disabled={isSubmitting}
                                                >
                                                    {config.renderThumbnail(displayUrl, t(`${i18nKey}.alt.previousUpload`))}
                                                    <span
                                                        className="delete-button"
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            requestPhotoDeletion(photo);
                                                        }}
                                                    >
                                                        ×
                                                    </span>
                                                </button>
                                            );
                                        })}
                                    </div>
                                </div>
                            ) : !isLoading && (
                                <p className="empty-message">{t(`${i18nKey}.empty`)}</p>
                            )}
                        </div>
                    }
                    footer={
                        <div className="profile-picture-upload-actions image-upload-modal-actions">
                            <Button
                                variant="secondary"
                                shape="pill"
                                className="image-upload-modal__button image-upload-modal__button--secondary"
                                onClick={onClose}
                                disabled={isSubmitting}
                            >
                                {t('common.cancel')}
                            </Button>
                            <Button
                                variant="primary"
                                shape="pill"
                                className="image-upload-modal__button image-upload-modal__button--primary"
                                onClick={handleUpload}
                                disabled={updateDisabled}
                            >
                                {isSubmitting ? t(`${i18nKey}.actions.updating`) : t(`${i18nKey}.actions.update`)}
                            </Button>
                        </div>
                    }
                />
            </PopupOverlay>
            {/* Sits above this card's own overlay (z 6000) on the same portal, so
                the destructive step is confirmed without leaving the upload flow. */}
            <ConfirmDeleteDialog
                isOpen={Boolean(photoPendingDeletion)}
                title={t(`${i18nKey}.confirmDelete`)}
                description={t(`${i18nKey}.confirmDeleteDescription`)}
                isSubmitting={isSubmitting}
                errorMessage={deleteError}
                onConfirm={handleDeletePhoto}
                onCancel={cancelPhotoDeletion}
            />
        </>,
        document.getElementById("portal")
    );
};

ProfilePictureUpload.propTypes = {
    onClose: PropTypes.func.isRequired,
    onUploadSuccess: PropTypes.func.isRequired,
    currentUrl: PropTypes.string,
    mode: PropTypes.oneOf(Object.keys(MEDIA_CONFIG)),
    mediaConfig: PropTypes.shape({
        i18nKey: PropTypes.string,
        getHistory: PropTypes.func,
        upload: PropTypes.func,
        selectExisting: PropTypes.func,
        deleteItem: PropTypes.func,
        cropperProps: PropTypes.object,
        thumbnailClassName: PropTypes.string,
        renderThumbnail: PropTypes.func,
        maxSizeMb: PropTypes.number,
    }),
};

ProfilePictureUpload.defaultProps = {
    currentUrl: null,
    mode: 'profilePicture',
    mediaConfig: null,
};

export default ProfilePictureUpload; 
